import React, { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNotifications } from "../../../context/NotificationContext";
import { Modal, Button, Checkbox, Card, Select, Row, Col, Space, Divider, Spin, Alert } from "antd";
import { PrinterOutlined, EyeOutlined } from "@ant-design/icons";

// Components
import PrintableOrder, { PrintableOrderOptions, PrintFormat } from "./PrintableOrder";

// API
import { fetchTestOrderPrintData } from "../../../api/healthcare/Testing/TestApi";

interface PrintOrderModalProps {
	isOpen: boolean;
	onClose: () => void;
	/**
	 * ⚠️ ORDER IDS, NEVER ORDER OBJECTS.
	 *
	 * The slip's patient, practitioner and clinic blocks are assembled server-side and
	 * fetched here by id, precisely so a caller cannot under-supply them. Several
	 * surfaces hold a `Test` with no `patient` attached — `TestingTab`'s row button
	 * passed the raw table record while its own row-click handler passed
	 * `{...record, patient}` — and that asymmetry is what made the patient block a dead
	 * branch on the printed slip. Taking ids makes that class of bug unrepresentable.
	 */
	orderIds: number[];
	title?: string;
}

const LAYOUT_OPTIONS = [
	{
		label: "Patient-facing request",
		options: [
			{ label: "Pathology Request (referral slip)", value: "pathology_request" },
			{ label: "Medicare-style pathology request", value: "medicare_request" },
		],
	},
	{
		label: "Internal worksheets",
		options: [
			{ label: "Individual Orders", value: "individual" },
			{ label: "Batch Print", value: "batch" },
			{ label: "Summary Report", value: "summary" },
		],
	},
];

const PrintOrderModal: React.FC<PrintOrderModalProps> = ({ isOpen, onClose, orderIds, title = "Print Test Orders" }) => {
	const [printOptions, setPrintOptions] = useState<PrintableOrderOptions>({
		includePatientDetails: true,
		includeOrderingProvider: true,
		includeTestDetails: true,
		includeSpecialInstructions: true,
		includeCompanyLogo: true,
		printFormat: "pathology_request",
		orientation: "portrait",
		fontSize: "medium",
	});

	const [selectedIds, setSelectedIds] = useState<number[] | null>(null);
	const [previewMode, setPreviewMode] = useState(false);

	const printRef = useRef<HTMLDivElement>(null);
	const { showError, showSuccess } = useNotifications();

	/**
	 * ⚠️ `needsMedicare` IS IN THE QUERY KEY DELIBERATELY.
	 *
	 * The server omits the Medicare identifiers entirely unless they are asked for, so
	 * switching to the Medicare layout genuinely needs a refetch. A key without it would
	 * render a Medicare form whose Medicare fields are permanently blank, with nothing on
	 * screen to explain why.
	 */
	const needsMedicare = printOptions.printFormat === "medicare_request";

	const {
		data: printData,
		isFetching,
		isError,
	} = useQuery({
		queryKey: ["test-order-print-data", orderIds, needsMedicare],
		queryFn: () => fetchTestOrderPrintData(orderIds, { includeMedicare: needsMedicare }),
		enabled: isOpen && orderIds.length > 0,
	});

	const allOrders = printData ?? [];
	// Default to everything until the practitioner deselects something; `null` means
	// "untouched", so a refetch does not silently reset an explicit selection.
	const selected = selectedIds === null ? allOrders : allOrders.filter((o) => selectedIds.includes(o.id));

	const handlePrint = () => {
		if (selected.length === 0) {
			showError("No orders selected for printing");
			return;
		}

		try {
			const printWindow = window.open("", "_blank");
			if (!printWindow) {
				showError("Unable to open print window. Please check your browser settings.");
				return;
			}

			const printContent = printRef.current?.innerHTML || "";

			printWindow.document.write(`
				<!DOCTYPE html>
				<html>
				<head>
					<title>Test Orders - ${new Date().toLocaleDateString()}</title>
					<meta charset="utf-8">
					<style>
						${getPrintStyles()}
					</style>
				</head>
				<body>
					${printContent}
				</body>
				</html>
			`);

			printWindow.document.close();

			printWindow.onload = () => {
				setTimeout(() => {
					printWindow.print();
					printWindow.close();
				}, 250);
			};

			showSuccess(`Printing ${selected.length} order(s)`);
			onClose();
		} catch (error) {
			console.error("Print error:", error);
			showError("Failed to print orders. Please try again.");
		}
	};

	const getPrintStyles = (): string => {
		const fontSize = printOptions.fontSize === "small" ? "11px" : printOptions.fontSize === "large" ? "14px" : "12px";

		return `
			@page {
				size: ${printOptions.orientation === "landscape" ? "A4 landscape" : "A4"};
				margin: 15mm;
			}

			* {
				margin: 0;
				padding: 0;
				box-sizing: border-box;
			}

			body {
				font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
				font-size: ${fontSize};
				line-height: 1.4;
				color: #1d1d1f;
				background: white;
			}

			.print-page {
				page-break-after: always;
				padding: 0;
			}

			.print-page:last-child {
				page-break-after: avoid;
			}

			/* =====================================================================
			   The requested-tests list.

			   ⚠️ THE ONLY UNBOUNDED BLOCK ON THE PAGE. An order can carry 50+ test
			   types, so the list flows in balanced columns and every entry is
			   break-inside: avoid — a test name split across a column or a page is
			   unreadable on a document someone has to check off by hand.
			   ===================================================================== */
			.test-list {
				column-gap: 18px;
				margin-top: 6px;
			}

			.test-list--cols-1 { column-count: 1; }
			.test-list--cols-2 { column-count: 2; }
			.test-list--cols-3 { column-count: 3; }

			.test-list-item {
				break-inside: avoid;
				page-break-inside: avoid;
				display: flex;
				align-items: baseline;
				gap: 6px;
				padding: 1px 0;
			}

			.test-list-name {
				flex: 1;
			}

			.test-tick {
				display: inline-block;
				width: 9px;
				height: 9px;
				border: 1px solid #1d1d1f;
				flex: 0 0 auto;
			}

			.test-group {
				break-inside: avoid-column;
				margin-bottom: 6px;
			}

			.test-group-title {
				font-size: 9px;
				font-weight: 700;
				text-transform: uppercase;
				letter-spacing: 0.6px;
				color: #6c6c70;
				border-bottom: 1px solid #d1d1d6;
				margin-bottom: 2px;
			}

			.test-list-empty {
				font-style: italic;
				color: #8e8e93;
			}

			/* =====================================================================
			   Request-slip layouts (pathology_request / medicare_request)
			   ===================================================================== */
			.req-page {
				/* Reserves the band the repeated continuation bar occupies in print. */
				padding-top: 4mm;
			}

			.req-continuation {
				display: flex;
				justify-content: space-between;
				font-size: 9px;
				color: #6c6c70;
				border-bottom: 1px solid #e5e5ea;
				padding-bottom: 2px;
				margin-bottom: 8px;
			}

			.req-letterhead {
				margin-bottom: 10px;
			}

			.req-clinic-name {
				font-size: 16px;
				font-weight: 700;
			}

			.req-clinic-meta {
				font-size: 10px;
				color: #6c6c70;
			}

			.req-title {
				font-size: 13px;
				font-weight: 700;
				letter-spacing: 1px;
				border-top: 2px solid #1d1d1f;
				border-bottom: 1px solid #1d1d1f;
				padding: 4px 0;
				margin-bottom: 10px;
				display: flex;
				justify-content: space-between;
				align-items: center;
			}

			.req-title--boxed {
				border: 2px solid #1d1d1f;
				padding: 4px 8px;
			}

			.req-urgent {
				font-size: 11px;
				border: 1.5px solid #1d1d1f;
				padding: 1px 8px;
			}

			.req-section {
				margin-bottom: 10px;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			/* The test list is the one section allowed to span pages. */
			.req-tests {
				break-inside: auto;
				page-break-inside: auto;
			}

			.req-section-title {
				font-size: 10px;
				font-weight: 700;
				text-transform: uppercase;
				letter-spacing: 0.6px;
				color: #6c6c70;
				margin-bottom: 4px;
			}

			.req-field-row {
				display: flex;
				align-items: baseline;
				gap: 6px;
				margin-bottom: 5px;
			}

			.req-field-label {
				font-size: 9px;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				color: #6c6c70;
				flex: 0 0 auto;
			}

			.req-field-label--date {
				margin-left: 10px;
			}

			/* A blank field is a RULED LINE to be completed by hand — never "N/A". */
			.req-rule {
				flex: 1 1 auto;
				border-bottom: 1px solid #1d1d1f;
				min-height: 14px;
				padding: 0 2px;
			}

			.req-rule--short { flex: 0 0 22%; }
			.req-rule--tiny  { flex: 0 0 10%; }
			.req-rule--mono  { font-family: 'Courier New', monospace; letter-spacing: 1px; }

			.req-packages {
				margin-bottom: 6px;
			}

			.req-packages-label {
				font-size: 9px;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				color: #6c6c70;
			}

			.req-warning {
				border: 1px solid #1d1d1f;
				padding: 4px 6px;
				margin-bottom: 8px;
				font-weight: 600;
				font-size: 11px;
			}

			.req-notes {
				border: 1px solid #d1d1d6;
				padding: 6px;
				min-height: 26px;
			}

			.req-flags {
				display: flex;
				gap: 16px;
				margin-top: 4px;
			}

			.req-checkbox {
				font-size: 10px;
				display: inline-flex;
				align-items: center;
				gap: 5px;
			}

			.req-checkbox::before {
				content: "";
				display: inline-block;
				width: 9px;
				height: 9px;
				border: 1px solid #1d1d1f;
			}

			.req-checkbox--ticked::before {
				background: #1d1d1f;
			}

			/*
			 * ⚠️ Flows AFTER the test list, never fixed — on a 50-test order the list
			 * decides which page this lands on, and a signature block pinned to page 1
			 * would sit above tests it is meant to authorise.
			 */
			.req-signature {
				margin-top: 14px;
				padding-top: 8px;
				border-top: 1px solid #1d1d1f;
				break-inside: avoid;
				page-break-inside: avoid;
			}

			.req-signature-row {
				display: flex;
				align-items: baseline;
				gap: 6px;
				margin-bottom: 8px;
			}

			/* =====================================================================
			   Internal worksheet layouts
			   ===================================================================== */
			.print-header {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 24px;
				padding-bottom: 12px;
				border-bottom: 2px solid #007aff;
			}

			.print-logo { font-size: 20px; font-weight: 600; color: #007aff; }
			.print-date { font-size: 11px; color: #8e8e93; }

			.order-card {
				border: 1px solid #d1d1d6;
				border-radius: 8px;
				margin-bottom: 16px;
				overflow: hidden;
				background: #fbfbfd;
			}

			.order-header {
				background: linear-gradient(135deg, #007aff, #5856d6);
				color: white;
				padding: 12px 16px;
				font-weight: 600;
			}

			.order-content { padding: 16px; }

			.info-grid {
				display: grid;
				grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
				gap: 12px;
				margin-bottom: 16px;
			}

			.info-item {
				background: white;
				padding: 8px 12px;
				border-radius: 6px;
				border: 1px solid #e5e5ea;
			}

			.info-label {
				font-size: 10px;
				font-weight: 600;
				color: #8e8e93;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				margin-bottom: 2px;
			}

			.info-value { font-weight: 500; color: #1d1d1f; }

			.test-details {
				background: #f2f2f7;
				border-radius: 6px;
				padding: 12px;
				margin-bottom: 12px;
			}

			.priority-badge {
				display: inline-block;
				padding: 2px 8px;
				border-radius: 12px;
				font-size: 10px;
				font-weight: 600;
				text-transform: uppercase;
			}

			.priority-urgent { background: #ff9500; color: white; }
			.priority-stat   { background: #ff3b30; color: white; }
			.priority-routine { background: #34c759; color: white; }

			.patient-section, .provider-section, .instructions-section { margin-bottom: 12px; }

			.section-title {
				font-size: 11px;
				font-weight: 600;
				color: #8e8e93;
				text-transform: uppercase;
				letter-spacing: 0.5px;
				margin-bottom: 6px;
				border-bottom: 1px solid #e5e5ea;
				padding-bottom: 2px;
			}

			.batch-summary {
				background: #f2f2f7;
				border-radius: 8px;
				padding: 16px;
				margin-bottom: 24px;
			}

			.summary-item {
				display: flex;
				justify-content: space-between;
				margin-bottom: 4px;
				padding: 2px 0;
			}

			.instructions-text {
				background: #fff3cd;
				border: 1px solid #ffeaa7;
				border-radius: 4px;
				padding: 8px;
				font-size: 11px;
				line-height: 1.5;
			}

			@media print {
				body {
					-webkit-print-color-adjust: exact;
					print-color-adjust: exact;
				}

				/*
				 * ⚠️ THE CONTINUATION BAR IS DELIBERATELY NOT REPEATED ON PAGES 2+.
				 *
				 * It identifies the patient at the top of the slip and no more. Repeating
				 * it per page needs either position:fixed — which Chrome does repeat,
				 * but with NO way to reserve space on the pages it repeats onto, so it
				 * overlays whatever is at the top of page 2 — or a <table><thead> wrapper,
				 * which fights the multi-column test list this layout depends on.
				 *
				 * On a 50-test pathology request, an overlay that hides test names is a
				 * worse failure than a page without a header: a collection centre would
				 * silently not collect them. Shipping no repeat is the honest trade.
				 * Revisit only with print-preview evidence, and never by pinning an
				 * element over the list.
				 */
			}
		`;
	};

	const toggleOrder = (orderId: number, checked: boolean) => {
		const current = selectedIds === null ? allOrders.map((o) => o.id) : selectedIds;
		setSelectedIds(checked ? [...current, orderId] : current.filter((id) => id !== orderId));
	};

	const toggleSelectAll = () => {
		setSelectedIds(selected.length === allOrders.length ? [] : allOrders.map((o) => o.id));
	};

	const printDisabled = isFetching || isError || selected.length === 0;

	return (
		<>
			<Modal
				title={
					<Space>
						<PrinterOutlined />
						<span>{title}</span>
						<span style={{ opacity: 0.65 }}>
							({orderIds.length} order{orderIds.length !== 1 ? "s" : ""})
						</span>
					</Space>
				}
				open={isOpen && !previewMode}
				onCancel={onClose}
				width="90vw"
				style={{ maxWidth: 800 }}
				footer={
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						<Button icon={<EyeOutlined />} onClick={() => setPreviewMode(true)} disabled={printDisabled}>
							Preview
						</Button>
						<Space>
							<Button onClick={onClose}>Cancel</Button>
							<Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint} disabled={printDisabled} loading={isFetching}>
								Print {selected.length} Order{selected.length !== 1 ? "s" : ""}
							</Button>
						</Space>
					</div>
				}
			>
				{isError && (
					<Alert
						type="error"
						showIcon
						style={{ marginBottom: 16 }}
						message="Could not load the order details"
						description="The printable details could not be fetched, so the slip would be incomplete. Close and try again."
					/>
				)}

				<Row gutter={[16, 16]}>
					{/* Print Options */}
					<Col xs={24} lg={12}>
						<Card title="Print Options" size="small">
							<Space direction="vertical" style={{ width: "100%" }} size="middle">
								<div>
									<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Layout</label>
									<Select
										value={printOptions.printFormat}
										onChange={(value: PrintFormat) => setPrintOptions((prev) => ({ ...prev, printFormat: value }))}
										style={{ width: "100%" }}
										options={LAYOUT_OPTIONS}
									/>
								</div>

								<Row gutter={8}>
									<Col span={12}>
										<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Orientation</label>
										<Select
											value={printOptions.orientation}
											onChange={(value) => setPrintOptions((prev) => ({ ...prev, orientation: value }))}
											style={{ width: "100%" }}
											options={[
												{ label: "Portrait", value: "portrait" },
												{ label: "Landscape", value: "landscape" },
											]}
										/>
									</Col>
									<Col span={12}>
										<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Font Size</label>
										<Select
											value={printOptions.fontSize}
											onChange={(value) => setPrintOptions((prev) => ({ ...prev, fontSize: value }))}
											style={{ width: "100%" }}
											options={[
												{ label: "Small (11px)", value: "small" },
												{ label: "Medium (12px)", value: "medium" },
												{ label: "Large (14px)", value: "large" },
											]}
										/>
									</Col>
								</Row>

								<Divider style={{ margin: "12px 0" }} />

								<Row gutter={[8, 8]}>
									<Col span={12}>
										<Checkbox
											checked={printOptions.includePatientDetails}
											onChange={(e) => setPrintOptions((prev) => ({ ...prev, includePatientDetails: e.target.checked }))}
										>
											Patient Details
										</Checkbox>
									</Col>
									<Col span={12}>
										<Checkbox
											checked={printOptions.includeOrderingProvider}
											onChange={(e) => setPrintOptions((prev) => ({ ...prev, includeOrderingProvider: e.target.checked }))}
										>
											Ordering Provider
										</Checkbox>
									</Col>
									<Col span={12}>
										<Checkbox
											checked={printOptions.includeTestDetails}
											onChange={(e) => setPrintOptions((prev) => ({ ...prev, includeTestDetails: e.target.checked }))}
										>
											Test Details
										</Checkbox>
									</Col>
									<Col span={12}>
										<Checkbox
											checked={printOptions.includeSpecialInstructions}
											onChange={(e) => setPrintOptions((prev) => ({ ...prev, includeSpecialInstructions: e.target.checked }))}
										>
											Instructions
										</Checkbox>
									</Col>
									<Col span={12}>
										<Checkbox
											checked={printOptions.includeCompanyLogo}
											onChange={(e) => setPrintOptions((prev) => ({ ...prev, includeCompanyLogo: e.target.checked }))}
										>
											Clinic Letterhead
										</Checkbox>
									</Col>
								</Row>
							</Space>
						</Card>
					</Col>

					{/* Order Selection */}
					<Col xs={24} lg={12}>
						<Card
							title="Order Selection"
							size="small"
							extra={<span style={{ fontSize: 12, opacity: 0.65 }}>{selected.length} selected</span>}
						>
							{isFetching ? (
								<div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
									<Spin />
								</div>
							) : (
								<>
									<div style={{ marginBottom: 12 }}>
										<Checkbox
											checked={selected.length === allOrders.length && allOrders.length > 0}
											onChange={toggleSelectAll}
										>
											<span style={{ fontWeight: 500 }}>Select All ({allOrders.length})</span>
										</Checkbox>
									</div>

									<div style={{ maxHeight: 300, overflow: "auto" }}>
										<Space direction="vertical" style={{ width: "100%" }} size="small">
											{allOrders.map((order) => (
												<div key={order.id} style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
													<Checkbox
														checked={selected.some((o) => o.id === order.id)}
														onChange={(e) => toggleOrder(order.id, e.target.checked)}
														style={{ width: "100%" }}
													>
														<div>
															<div style={{ fontWeight: 500, fontSize: 14 }}>
																{[order.patient?.first_name, order.patient?.last_name].filter(Boolean).join(" ") ||
																	`Order #${order.id}`}
															</div>
															<div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
																{/* Never `{order.test_type}` — that array rendered as one
																    concatenated slug string. */}
																{order.test_count} test{order.test_count !== 1 ? "s" : ""} •{" "}
																{order.priority?.toUpperCase()}
															</div>
														</div>
													</Checkbox>
												</div>
											))}
										</Space>
									</div>
								</>
							)}
						</Card>
					</Col>
				</Row>

				{/* Hidden print content */}
				<div style={{ display: "none" }}>
					<div ref={printRef}>
						<PrintableOrder orders={selected} options={printOptions} />
					</div>
				</div>
			</Modal>

			{/* Preview Modal */}
			{previewMode && (
				<Modal
					title="Print Preview"
					open={previewMode}
					onCancel={() => setPreviewMode(false)}
					width="95vw"
					style={{ top: 20 }}
					styles={{ body: { height: "calc(95vh - 110px)", overflow: "auto" } }}
					footer={
						<Space>
							<Button onClick={() => setPreviewMode(false)}>Close Preview</Button>
							<Button
								type="primary"
								icon={<PrinterOutlined />}
								onClick={() => {
									setPreviewMode(false);
									handlePrint();
								}}
							>
								Print
							</Button>
						</Space>
					}
				>
					<div style={{ background: "#f5f5f7", padding: 20, borderRadius: 8, maxHeight: "calc(95vh - 200px)", overflow: "auto" }}>
						<style>{getPrintStyles()}</style>
						<div
							style={{
								background: "white",
								boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
								borderRadius: 8,
								padding: 40,
								fontSize: printOptions.fontSize === "small" ? 11 : printOptions.fontSize === "large" ? 14 : 12,
							}}
						>
							<PrintableOrder orders={selected} options={printOptions} />
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};

export default PrintOrderModal;
